import { useMemo, useRef } from 'react';
import type { CSSProperties } from 'react';
import type { FeaturedProject } from '../../content/site';
import { fr } from '../../content/typography';
import { gsap, useGSAP, ScrollTrigger, EASE, MOTION_OK, REDUCED } from '../../lib/gsap';
import { prefersReducedMotion } from '../../lib/useReducedMotion';
import { compare } from './ngram';
import type { GramOrigin } from './ngram';

/** Labels of the panel (site.work.ngramLabels). */
type NgramLabels = { query: string; document: string; similarity: string };

const gramsWord = (n: number) => (n === 2 ? 'bigrammes' : n === 3 ? 'trigrammes' : `${n}-grammes`);
const sharedLabel = (shared: number, total: number, unit = '') =>
	`${shared}${unit ? ` ${unit}` : ''} en commun sur ${total}`;
const formatScore = (value: number) =>
	new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);

/** Token values, for the colour tweens (tailwind.config.js). */
const MINERAL = '#F7F7F0';
const GRAPHITE = '#252B27';
const FOREST = '#294D3B';
const SOLAR = '#E8C96A';
const SOLAR_CLEAR = 'rgba(232, 201, 106, 0)';

/** A space, made visible the way typesetters mark it: a flat bracket on the baseline. */
function Space({ soft = false }: { soft?: boolean }) {
	return (
		<span
			className={`inline-block h-[0.2em] w-[0.74ch] border-x border-b border-current ${
				soft ? 'opacity-50' : 'opacity-55'
			}`}
		/>
	);
}

/** One gram in its chip: one monospace cell per character, spaces made visible. */
function Gram({ value }: { value: string }) {
	return (
		<>
			{value.split('').map((ch, i) => (
				<span key={i} className="inline-block w-[1ch] text-center">
					{ch === ' ' ? <Space /> : ch}
				</span>
			))}
		</>
	);
}

type LineProps = {
	kind: 'query' | 'document';
	/** The padded text, as the engine reads it. */
	text: string;
	/** Characters covered by a shared gram. */
	cover: boolean[];
	n: number;
	/** Largest font size; below it the line is fitted to the panel width. */
	max: string;
};

/**
 * A text set one monospace cell per character so the window (n cells wide) can slide by
 * exactly one character per step. The Forest underline marks the characters that belong
 * to a gram both texts share.
 */
function Line({ kind, text, cover, n, max }: LineProps) {
	const chars = text.split('');
	const style: CSSProperties = { fontSize: `min(${max}, calc(100cqi / ${(chars.length * 0.6).toFixed(2)}))` };

	return (
		<div
			data-ng-line={kind}
			className="relative w-max max-w-full whitespace-nowrap font-mono font-medium leading-[1.55]"
			style={style}
		>
			<span
				data-ng-window
				className="pointer-events-none invisible absolute bottom-[0.16em] left-0 top-[0.34em] opacity-0"
				style={{ width: `${n}ch` }}
			>
				<span className="absolute inset-y-0 -left-[0.12ch] -right-[0.12ch] rounded-[0.26em] bg-solar" />
			</span>
			{chars.map((ch, i) => {
				const on = cover[i];
				return (
					<span key={i} className="relative inline-block w-[1ch] text-center">
						{ch === ' ' ? <Space soft /> : ch}
						{on && (
							<span
								data-ng-mark={i}
								className={`absolute bottom-0 left-0 h-[0.13em] w-full origin-left bg-forest ${
									cover[i - 1] ? '' : 'rounded-l-full'
								} ${cover[i + 1] ? '' : 'rounded-r-full'}`}
							/>
						)}
					</span>
				);
			})}
		</div>
	);
}

const CHIP: Record<GramOrigin, string> = {
	query: 'border-transparent bg-mineral text-graphite',
	shared: 'border-transparent bg-forest text-mineral',
	document: 'border-dashed border-graphite/45 text-graphite',
};

const LABEL = 'text-[0.8125rem] font-medium leading-none text-graphite';

/**
 * Brn, ex-3ngram (a memory server for AI agents) finds what an agent asks for even when the
 * words differ, with fuzzy trigram search. The panel shows that search itself, live: the
 * sample from site.ts is cut into character n-grams by a window sliding one character at
 * a time, the grams settle into a set (a repeat is pulsed, not added), then the document
 * from site.ts is scanned and every gram both texts share lights up in the set and in
 * both lines. The counts and the Jaccard similarity are computed from the strings at
 * render time with the engine's own method (see ngram.ts): nothing is typed in by hand.
 *
 * Colour roles: Solar is the window (the active state), Forest marks what is shared,
 * Mineral chips are the query's other grams, dashed chips the document's own.
 * Motion: one pass of under five seconds when the panel comes into view, transforms,
 * opacity and colours only; afterwards, hovering a gram shows where it occurs.
 * Reduced motion: the finished state, static (it is what the markup renders; the
 * timeline only sets the starting state when motion is allowed).
 * The drawing is aria-hidden; a sentence with the same numbers describes it.
 */
export function NgramPanel({ project, labels }: { project: FeaturedProject; labels: NgramLabels }) {
	const root = useRef<HTMLElement>(null);
	/** False while the intro plays: pointer exploration waits for it to finish. */
	const ready = useRef(true);
	const sample = project.ngram?.sample ?? '';
	const size = project.ngram?.size ?? 3;
	const docText = project.ngram?.document ?? '';

	const cmp = useMemo(() => compare(sample, docText, size), [sample, docText, size]);
	const { n, query, doc, union, shared, similarity, queryCover, docCover } = cmp;
	const score = formatScore(similarity);
	const words = gramsWord(n);
	const groups = [
		union.map((item, i) => ({ ...item, i })).filter((item) => item.origin !== 'document'),
		union.map((item, i) => ({ ...item, i })).filter((item) => item.origin === 'document'),
	];

	// The query's grams fill whole rows (3 rows wide, 5 on phones); the document's own
	// grams sit in a second grid of the same columns, just below.
	const columns = {
		'--ng-cols-s': Math.min(7, Math.ceil(query.unique.length / 5)),
		'--ng-cols-l': Math.min(12, Math.ceil(query.unique.length / 3)),
	} as CSSProperties;

	const caption = fr(
		`La requête « ${sample} » est découpée en ${query.unique.length} ${words}, puis comparée au document « ${docText} » : ${sharedLabel(
			shared,
			union.length,
			words,
		)}, similarité de ${score}.`,
	);

	const { contextSafe } = useGSAP(
		() => {
			const el = root.current;
			if (!el) return;
			const mm = gsap.matchMedia();

			mm.add({ motion: MOTION_OK, reduced: REDUCED }, (context) => {
				// Reduced motion: the markup already is the finished, static state.
				if (context.conditions?.reduced) {
					ready.current = true;
					return;
				}

				const qWin = el.querySelector<HTMLElement>('[data-ng-line="query"] [data-ng-window]');
				const dWin = el.querySelector<HTMLElement>('[data-ng-line="document"] [data-ng-window]');
				const counter = el.querySelector<HTMLElement>('[data-ng-count]');
				const result = el.querySelector<HTMLElement>('[data-ng-result]');
				const chips = gsap.utils.toArray<HTMLElement>('[data-ng-chip]', el);
				if (!qWin || !dWin || !counter || !result || chips.length !== union.length) return;

				const marks = (kind: string) => {
					const byIndex: HTMLElement[] = [];
					el.querySelectorAll<HTMLElement>(`[data-ng-line="${kind}"] [data-ng-mark]`).forEach((mark) => {
						byIndex[Number(mark.dataset.ngMark)] = mark;
					});
					return byIndex;
				};
				const qMarks = marks('query');
				const dMarks = marks('document');
				const chipOf = new Map<string, HTMLElement>();
				union.forEach((item, i) => chipOf.set(item.gram, chips[i]));
				const isShared = new Set(union.filter((item) => item.origin === 'shared').map((item) => item.gram));
				const span = (start: number, list: HTMLElement[]) =>
					Array.from({ length: n }, (_, k) => list[start + k]).filter((mark): mark is HTMLElement => Boolean(mark));
				const setCount = (value: number) => {
					counter.textContent = String(value);
				};

				// Starting state: empty set, no marks, no result, shared grams not lit yet.
				gsap.set(chips, { autoAlpha: 0, scale: 0.6 });
				gsap.set(
					chips.filter((_, i) => union[i].origin === 'shared'),
					{ backgroundColor: MINERAL, color: GRAPHITE },
				);
				gsap.set([...qMarks, ...dMarks].filter(Boolean), { scaleX: 0 });
				gsap.set(result, { autoAlpha: 0, y: 14 });
				setCount(0);

				ready.current = false;
				const tl = gsap.timeline({
					paused: true,
					defaults: { ease: EASE },
					onComplete: () => {
						ready.current = true;
					},
				});
				let t = 0.2;
				tl.to(qWin, { autoAlpha: 1, duration: 0.3 }, 0);

				// 1. Cut: the window slides one character per step, slow first, then brisk.
				const indexed = new Set<string>();
				query.windows.forEach(({ gram, start }, i) => {
					const step = 0.05 + 0.24 * Math.exp(-i / 2.2);
					const chip = chipOf.get(gram);
					if (i > 0) tl.to(qWin, { xPercent: (start * 100) / n, duration: step * 0.85, ease: 'power2.out' }, t);
					if (chip && !indexed.has(gram)) {
						indexed.add(gram);
						const count = indexed.size;
						tl.to(chip, { autoAlpha: 1, scale: 1, duration: 0.5 }, t);
						tl.fromTo(
							chip,
							{ backgroundColor: SOLAR },
							{ backgroundColor: MINERAL, duration: 0.6, ease: 'power1.out', immediateRender: false },
							t,
						);
						tl.call(setCount, [count], t);
					} else if (chip) {
						// Already in the set: counted once, so it only pulses.
						tl.fromTo(
							chip,
							{ scale: 1.16, backgroundColor: SOLAR },
							{ scale: 1, backgroundColor: MINERAL, duration: 0.55, immediateRender: false },
							t,
						);
					}
					t += step;
				});

				// 2. Compare: the document is scanned; shared grams light up everywhere.
				t += 0.12;
				tl.to(qWin, { autoAlpha: 0, duration: 0.25 }, t);
				tl.to(dWin, { autoAlpha: 1, duration: 0.2 }, t);
				const lit = new Set<string>();
				const added = new Set<string>();
				doc.windows.forEach(({ gram, start }, j) => {
					const chip = chipOf.get(gram);
					if (j > 0) tl.to(dWin, { xPercent: (start * 100) / n, duration: 0.03, ease: 'none' }, t);
					if (chip && isShared.has(gram)) {
						if (!lit.has(gram)) {
							lit.add(gram);
							// The ink flips once the fill is mostly Forest, so the gram stays legible.
							tl.to(chip, { backgroundColor: FOREST, duration: 0.4, ease: 'power1.out' }, t);
							tl.set(chip, { color: MINERAL }, t + 0.2);
							tl.fromTo(chip, { scale: 1.12 }, { scale: 1, duration: 0.45, immediateRender: false }, t);
							query.windows.forEach((w) => {
								if (w.gram === gram) tl.to(span(w.start, qMarks), { scaleX: 1, duration: 0.32, ease: 'power2.out' }, t);
							});
						}
						tl.to(span(start, dMarks), { scaleX: 1, duration: 0.26, ease: 'power2.out' }, t);
					} else if (chip && !added.has(gram)) {
						added.add(gram);
						const count = indexed.size + added.size;
						tl.to(chip, { autoAlpha: 1, scale: 1, duration: 0.45 }, t);
						tl.fromTo(
							chip,
							{ backgroundColor: SOLAR },
							{ backgroundColor: SOLAR_CLEAR, duration: 0.6, ease: 'power1.out', immediateRender: false },
							t,
						);
						tl.call(setCount, [count], t);
					}
					t += 0.034;
				});

				// 3. Result.
				tl.to(dWin, { autoAlpha: 0, duration: 0.3 }, t);
				tl.to(result, { autoAlpha: 1, y: 0, duration: 0.9 }, t + 0.05);

				ScrollTrigger.create({ trigger: el, start: 'top 62%', once: true, onEnter: () => tl.play() });

				return () => {
					ready.current = true;
					setCount(union.length);
				};
			});
		},
		{ scope: root, dependencies: [cmp] },
	);

	/**
	 * Pointer exploration, once the intro is over: hovering a gram moves the window onto
	 * its first occurrence in each text (and hides it where the gram does not occur).
	 * Feedback only, pointer only: the same facts are in the caption. Instant under
	 * reduced motion.
	 */
	const explore = contextSafe((index: number | null) => {
		const el = root.current;
		if (!el || !ready.current) return;
		const gram = index === null ? null : union[index]?.gram ?? null;
		const instant = prefersReducedMotion();
		const lines = [
			{ scan: query, win: el.querySelector<HTMLElement>('[data-ng-line="query"] [data-ng-window]') },
			{ scan: doc, win: el.querySelector<HTMLElement>('[data-ng-line="document"] [data-ng-window]') },
		];
		lines.forEach(({ scan, win }) => {
			if (!win) return;
			const hit = gram === null ? undefined : scan.windows.find((w) => w.gram === gram);
			if (!hit) {
				gsap.to(win, { autoAlpha: 0, duration: instant ? 0 : 0.2, overwrite: 'auto' });
				return;
			}
			const x = (hit.start * 100) / n;
			const shown = Number(gsap.getProperty(win, 'opacity')) > 0.01;
			if (instant || !shown) gsap.set(win, { xPercent: x });
			else gsap.to(win, { xPercent: x, duration: 0.28, ease: 'power2.out', overwrite: 'auto' });
			gsap.to(win, { autoAlpha: 1, duration: instant ? 0 : 0.18, overwrite: 'auto' });
		});
	});

	return (
		<figure ref={root}>
			{/* The caption says what the panel is: a demo of Brn's trigram search, not a project title. */}
			<figcaption className="mb-4 max-w-prose text-[0.9375rem] leading-relaxed text-graphite-muted dark:text-dusk-muted md:mb-5">
				{caption}
			</figcaption>
			{/* The panel is a self-contained light object, like a screenshot: same in both themes. */}
			<div className="overflow-hidden rounded-card bg-sage p-[clamp(1.25rem,3.4vw,2.75rem)] text-graphite shadow-[0_24px_48px_-26px_rgba(41,77,59,0.55)] dark:ring-1 dark:ring-mineral/10">
				<div aria-hidden className="[container-type:inline-size]">
					<p className={LABEL}>{labels.query}</p>
					<div className="mt-[clamp(0.5rem,1.6cqi,0.875rem)]">
						<Line kind="query" text={query.padded} cover={queryCover} n={n} max="2.25rem" />
					</div>

					<p className={`${LABEL} mt-[clamp(1.25rem,4.4cqi,2.25rem)]`}>
						<span data-ng-count className="tabular-nums">
							{union.length}
						</span>{' '}
						{words}
					</p>
					<div
						className="mt-[clamp(0.625rem,1.8cqi,1rem)]"
						style={columns}
						onPointerOver={(event) => {
							const chip = (event.target as HTMLElement).closest<HTMLElement>('[data-ng-chip]');
							if (chip) explore(Number(chip.dataset.ngChip));
						}}
						onPointerLeave={() => explore(null)}
					>
						{groups.map((group, g) =>
							group.length ? (
								<div
									key={g}
									className={`grid grid-cols-[repeat(var(--ng-cols-s),minmax(0,1fr))] gap-[clamp(0.25rem,0.9cqi,0.4375rem)] [@container(min-width:26rem)]:grid-cols-[repeat(var(--ng-cols-l),minmax(0,1fr))] ${
										g > 0 ? 'mt-[clamp(0.5rem,1.6cqi,0.875rem)]' : ''
									}`}
								>
									{group.map((item) => (
										<span
											key={item.gram}
											data-ng-chip={item.i}
											className={`flex items-center justify-center rounded-full border py-[0.5em] font-mono text-[length:clamp(0.6875rem,2.05cqi,0.875rem)] font-medium leading-none hover:ring-2 hover:ring-solar ${
												CHIP[item.origin]
											}`}
										>
											<Gram value={item.gram} />
										</span>
									))}
								</div>
							) : null,
						)}
					</div>

					<p className={`${LABEL} mt-[clamp(1.25rem,4.4cqi,2.25rem)]`}>{labels.document}</p>
					<div className="mt-[clamp(0.5rem,1.6cqi,0.875rem)]">
						<Line kind="document" text={doc.padded} cover={docCover} n={n} max="1.375rem" />
					</div>

					<div className="mt-[clamp(1.25rem,4cqi,2rem)] border-t border-graphite/15 pt-[clamp(1rem,3cqi,1.5rem)]">
						<div data-ng-result className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
							<p className="flex items-center gap-2.5 text-[0.9375rem] font-medium leading-none">
								<span className="size-3 shrink-0 rounded-full bg-forest" />
								{sharedLabel(shared, union.length)}
							</p>
							<p className="flex items-baseline gap-3">
								<span className="text-[0.9375rem] font-medium leading-none">{labels.similarity}</span>
								<span className="font-display text-[length:clamp(2.25rem,7cqi,3.5rem)] font-bold leading-[0.9] tracking-[-0.04em]">
									{score}
								</span>
							</p>
						</div>
					</div>
				</div>
			</div>
		</figure>
	);
}
